import type { DeepPartial } from 'react-hook-form';
import type { TransactionFormValues } from './MobileTransactionForm';

/** The coding fields a mobile save can write, in the order buildCodingFieldUpdates handles them. */
export const MOBILE_CODING_FIELDS = [
  'glCode',
  'projectCode',
  'costCode',
  'costType',
  'subJob',
  'vendor',
  'location',
  'department',
  'subaccount',
  'employee',
  'billableItem',
  'equipment',
  'emCostCode',
  'emCostType',
  'codingType',
] as const;
export type MobileCodingField = (typeof MOBILE_CODING_FIELDS)[number];

const nonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * What makes two values of a field "the same": the id, the posting-account id, or the
 * upper-cased type. A re-picked option is another object, with __typename and more fields, for
 * the same id, so the values are compared by identity and never deeply. null, undefined and ''
 * all read as null.
 */
export const codingIdentity = (field: MobileCodingField, value: unknown): string | null => {
  if (field === 'codingType') return nonEmpty(value)?.toUpperCase() ?? null;
  if (!value || typeof value !== 'object') return null;
  if (field === 'billableItem') return nonEmpty((value as { accountId?: unknown }).accountId);
  return nonEmpty((value as { id?: unknown }).id);
};

/**
 * SEE-1899: the fields whose identity differs between what the sheet opened with and what is
 * being saved. The baseline is the opened values, not the row: where the sheet hides coding it
 * opens every field as null, and the row's stored ids must not read as changed to null.
 */
export const changedCodingFields = (
  // RHF 7.44 types formState.defaultValues as Readonly<DeepPartial<…>>.
  opened: DeepPartial<TransactionFormValues> | undefined,
  current: TransactionFormValues
): Set<MobileCodingField> =>
  new Set(
    MOBILE_CODING_FIELDS.filter(
      (field) => codingIdentity(field, opened?.[field]) !== codingIdentity(field, current[field])
    )
  );
